import { getWindowData } from 'owa-window-data';

let parsedQueryString: Record<string, string> | null = null;
let sourceParam: string | undefined = undefined;
let removeQueryStringCache: boolean = false;

/**
 * Set the caching behavior for query string parsing.
 * Called by feature flag initialization code via configureQueryStringCaching based on flight 'fwk-removeQueryStringCache'.
 * Inverting dependency to avoid circular dependency with "isFeatureEnabled"
 */
export function setQueryStringCachingBehavior(removeCache: boolean): void {
    removeQueryStringCache = removeCache;
}

export function getQueryStringParameters(
    locationObj: Pick<Location, 'search'> | undefined = undefined
): { [key: string]: string } {
    if (!locationObj) {
        locationObj = getWindowData()?.location;
    }

    if (removeQueryStringCache) {
        return parseQuery(locationObj);
    } else {
        if (locationObj === location) {
            // Cache the parsed value for the main window
            return parsedQueryString || (parsedQueryString = parseQuery(location));
        } else {
            // Parsing the querystring for an iframe or such
            return parseQuery(locationObj);
        }
    }
}

export function updateQueryStringCache(): void {
    parsedQueryString = parseQuery(location);
}

export function getQueryStringParameter(key: string): string {
    if (parsedQueryString) {
        return parsedQueryString[key];
    }
    return getQueryStringParameters()[key];
}

export function hasQueryStringParameter(key: string): boolean {
    if (parsedQueryString) {
        return key in parsedQueryString;
    }
    return Object.prototype.hasOwnProperty.call(getQueryStringParameters(), key);
}

function parseQuery(locationObj: Pick<Location, 'search'>) {
    if (typeof locationObj !== 'undefined') {
        return locationObj.search ? nodeParseQueryString(locationObj.search.substr(1)) : {};
    }

    // We're running outside a browser window, so return an empty property bag
    return {};
}

export function getFromCodeQueryParam(): string | undefined {
    const fromCode = getQueryStringParameter('FromCode');
    if (fromCode) {
        return fromCode.substring(0, 11);
    }
    return fromCode;
}

export function getSourceQueryParam(): string | undefined {
    return sourceParam || (sourceParam = getAndStripSourceQueryParam());
}

function getAndStripSourceQueryParam(): string | undefined {
    const source = getQueryStringParameter('source');
    if (source) {
        const newSearch = removeQuery(location.search, 'source');
        history.replaceState(history.state, '/', location.pathname + newSearch);
    }
    return source;
}

export function removeQuery(search: string, queryKey: string): string {
    const regex = new RegExp('(\\?|&)' + queryKey + '=\\w*&?', 'g');
    let newSearch = search.split(regex).join('');
    const lastChar = newSearch[newSearch.length - 1];
    if (lastChar === '?' || lastChar === '&') {
        newSearch = newSearch.substr(0, newSearch.length - 1);
    }
    return newSearch;
}

const maxKeys = 1000;
const regexp = /\+/g;
export function nodeParseQueryString(qs: string): { [key: string]: string } {
    const result: Record<string, string> = {};
    if (typeof qs === 'string' && qs.length > 0) {
        const qsParts = qs.split('&');
        const length = qsParts.length > maxKeys ? maxKeys : qsParts.length;
        for (let i = 0; i < length; ++i) {
            const qsPartSplit = qsParts[i].replace(regexp, '%20').split('=');
            result[decodeURIComponent(qsPartSplit[0])] = decodeURIComponent(
                qsPartSplit.slice(1).join('=')
            );
        }
    }

    return result;
}

function trimQuestionMark(qs: string): string {
    return qs.startsWith('?') ? qs.slice(1) : qs;
}

/*
   Merges two query strings.
   If a key exists in both query strings, the value from newQuery will overwrite the first instance in existingQuery's value.
   Returns a merged encoded query string, prefixed with '?'
*/
export function mergeQueryStrings(existingQuery: string, newQuery?: string): string {
    if (!newQuery) {
        return existingQuery;
    }

    if (!existingQuery) {
        return newQuery.startsWith('?') ? newQuery : '?' + newQuery;
    }

    const existingPairs = nodeParseQueryString(trimQuestionMark(existingQuery));
    const newPairs = nodeParseQueryString(trimQuestionMark(newQuery));

    // Ensure newPairs is second to overwrite any duplicates that may be in existingPairs
    const mergedPairs = { ...existingPairs, ...newPairs };

    // serialize back to query string
    const mergedString = stringify(mergedPairs);

    return mergedString ? '?' + mergedString : '';
}

export function stringify(params: { [key: string]: string }): string {
    if (params) {
        return Object.keys(params)
            .map(
                k => encodeURIComponent(k) + (params[k] ? '=' + encodeURIComponent(params[k]) : '')
            )
            .join('&');
    }
    return '';
}

export function test_reset(): void {
    parsedQueryString = null;
    sourceParam = undefined;
    removeQueryStringCache = false; // Reset to default
}
